import { v4 as uuidv4 } from 'uuid';
import { InterviewQuestion, InterviewQuestions, InterviewQuestionsChildren } from '@/types';
import { GroupContentType } from '@/enums';

/**
 * Type guard to check if a child is a group/subgroup (has children array)
 */
export const isGroup = (
  item: InterviewQuestion | InterviewQuestions,
): item is InterviewQuestions => {
  return 'children' in item && Array.isArray(item.children);
};

/**
 * Type guard to check if a child is a question (has question field)
 */
export const isQuestion = (
  item: InterviewQuestion | InterviewQuestions,
): item is InterviewQuestion => {
  return 'question' in item;
};

/**
 * Determines if we should render the group UI or flat question list
 * Groups should be visible when:
 * 1. There are multiple root-level groups (children contains multiple groups) OR
 * 2. There is at least one subgroup within the single root group
 */
export const shouldRenderGroupUI = (interviewQuestions: InterviewQuestions): boolean => {
  if (!interviewQuestions?.children || interviewQuestions.children.length === 0) {
    return false;
  }

  const { children } = interviewQuestions;

  // Check if all children are questions (no groups) - initial/flat state
  const allChildrenAreQuestions = children.every(isQuestion);

  if (allChildrenAreQuestions) {
    return false; // Render flat list
  }

  // If any child is a group, we should render group UI
  return children.some(isGroup);
};

/**
 * Gets the content type of a group's children
 * Returns 'questions', 'groups' or 'empty'
 */
export const getGroupContentType = (group: InterviewQuestions): GroupContentType => {
  if (!group.children || group.children.length === 0) {
    return GroupContentType.EMPTY;
  }

  const hasQuestions = group.children.some(isQuestion);
  const hasGroups = group.children.some(isGroup);

  if (hasQuestions) {
    return GroupContentType.QUESTIONS;
  }
  if (hasGroups) {
    return GroupContentType.GROUPS;
  }

  return GroupContentType.EMPTY;
};

/**
 * Creates a new empty group structure with a unique ID
 */
export const createEmptyGroup = (name: string = 'New group'): InterviewQuestions => {
  return {
    id: uuidv4(), // Assign unique ID to avoid conflicts when dragging
    name,
    randomize: false,
    children: [],
  };
};

/**
 * Creates a new empty question
 */
export const createEmptyQuestion = (): InterviewQuestion => {
  return {
    id: uuidv4(),
    question: '',
    follow_up_question: '0',
    answer_quantification: [
      {
        quantify_answer: false,
        value: '',
      },
    ],
  };
};

/**
 * Flattens a group structure into a flat array of questions
 * Used for backward compatibility and when rendering flat view
 */
export const flattenGroupToQuestions = (group: InterviewQuestions): InterviewQuestion[] => {
  const questions: InterviewQuestion[] = [];

  const traverse = (item: InterviewQuestions | InterviewQuestion) => {
    if (isQuestion(item)) {
      questions.push(item);
    } else if (isGroup(item)) {
      item.children.forEach(traverse);
    }
  };

  if (group.children) {
    group.children.forEach(traverse);
  }

  return questions;
};

/**
 * Wraps flat questions array into a single group structure
 * Used when transforming frontend data to backend format
 */
export const wrapQuestionsInGroup = (
  questions: InterviewQuestion[],
  groupName: string = 'Interview Questions',
  randomize: boolean = false,
): InterviewQuestions => {
  return {
    id: uuidv4(),
    name: groupName,
    randomize,
    children: questions,
  };
};

/**
 * Finds an item (question or group) and returns its path in the tree
 */
export const findItemPath = (
  itemId: string,
  isQuestionItem: boolean,
  groupStructure: InterviewQuestions,
): number[] | null => {
  const search = (group: InterviewQuestions, path: number[]): number[] | null => {
    for (let i = 0; i < group.children.length; i += 1) {
      const child = group.children[i];

      if (isQuestionItem && isQuestion(child) && child.id === itemId) {
        return [...path, i];
      }

      // For groups, check both ID and name for backward compatibility
      if (
        !isQuestionItem &&
        isGroup(child) &&
        ((child.id && child.id === itemId) || child.name === itemId)
      ) {
        return [...path, i];
      }

      if (isGroup(child)) {
        const result = search(child, [...path, i]);
        if (result) return result;
      }
    }
    return null;
  };

  return search(groupStructure, []);
};

/**
 * Gets a group at a specific path in the tree
 */
export const getGroupAtPath = (
  structure: InterviewQuestions,
  path: number[],
): InterviewQuestions | null => {
  let current = structure;
  for (let i = 0; i < path.length; i += 1) {
    const idx = path[i];
    const child = current.children[idx];
    if (isGroup(child)) {
      current = child;
    } else {
      return null;
    }
  }
  return current;
};

/**
 * Finds a subgroup by ID in the tree
 */
export const findSubgroupById = (
  structure: InterviewQuestions,
  targetId: string,
): InterviewQuestions | null => {
  // Check each child group
  const groups = structure.children.filter(isGroup);

  // First check if any direct child matches
  const directMatch = groups.find((child) => (child.id || child.name) === targetId);
  if (directMatch) return directMatch;

  // Recursively search in child groups
  // eslint-disable-next-line no-restricted-syntax
  for (const child of groups) {
    const found = findSubgroupById(child, targetId);
    if (found) return found;
  }

  return null;
};

/**
 * Removes a question from the tree by ID
 */
export const removeQuestionFromTree = (
  structure: InterviewQuestions,
  questionId: string,
): InterviewQuestions => {
  return {
    ...structure,
    children: structure.children
      .filter((child) => !isQuestion(child) || child.id !== questionId)
      .map((child) => {
        if (isGroup(child)) {
          return removeQuestionFromTree(child, questionId);
        }
        return child;
      }) as InterviewQuestionsChildren,
  };
};

/**
 * Removes a subgroup from the tree by ID
 */
export const removeSubgroupFromTree = (
  structure: InterviewQuestions,
  subgroupId: string,
): InterviewQuestions => {
  return {
    ...structure,
    children: structure.children
      .filter((child) => !isGroup(child) || (child.id || child.name) !== subgroupId)
      .map((child) => {
        if (isGroup(child)) {
          return removeSubgroupFromTree(child, subgroupId);
        }
        return child;
      }) as InterviewQuestionsChildren,
  };
};

/**
 * Checks if any question in the group structure has empty text
 */
export const hasEmptyQuestions = (structure: InterviewQuestions): boolean => {
  const allQuestions = flattenGroupToQuestions(structure);
  return allQuestions.some(({ question }) => !question || question.trim() === '');
};

/**
 * Checks if the interview questions structure is empty (no children or all empty)
 */
export const isInterviewQuestionsEmpty = (structure?: InterviewQuestions): boolean => {
  if (!structure?.children || structure.children.length === 0) {
    return true;
  }

  // Recursively check all children
  return structure.children.every((child) => {
    if (isQuestion(child)) {
      // A question is empty if it has no text or only whitespace
      return !child.question || child.question.trim() === '';
    }
    if (isGroup(child)) {
      // Recursively check if the group is empty
      return isInterviewQuestionsEmpty(child);
    }
    return true;
  });
};
